import axios from 'axios'
import QueryParamsUtil from 'api/utils/query-params-util'

const API_PATH = 'api/v2/admins/sellers/outbounds/returns'

const buildFormDataPayload = (values = {}) => {
  const formData = new FormData()

  Object.keys(values).forEach((key) => {
    const value = values[key]

    if (value === undefined || value === null || value === '') return

    formData.append(`seller_return[${key}]`, value)
  })

  return formData
}

const SellerOutboundReturnsApiV2 = {

  /**
   * Index Seller Outbound Return
   * @param {object} tableState - ReactTable final state
   * @param {object} additionalFilters - A plain object to get customize filters outside `tableState`
   */
  get: async ({ tableState, additionalFilters = {} } = {}) => {
    const params = QueryParamsUtil.format(tableState, additionalFilters)
    const path   = API_PATH

    const response = axios.get(path, { params })
    return response
  },

  /**
   * Show Seller Outbound Return
   * @param {number} id - Seller Outbound Return ID
   */
  show: async (id) => {
    const path = `${API_PATH}/${id}`

    const response = axios.get(path)
    return response
  },

  /**
   * Estimate Consignor Return shipping cost from backend.
   * Cost calculation must follow seller address and DealsManager/CartsManager shipment logic.
   */
  estimateShippingCost: async (values) => {
    const path = `${API_PATH}/estimate_shipping_cost`
    const payload = {
      seller_return: values
    }

    const response = axios.post(path, payload)
    return response
  },

  /**
   * Create Seller Outbound Return
   * @param {object} payload - API payload
   */
  create: async (values) => {
    const path     = `${API_PATH}`
    const payload  = {
      seller_return: values
    }
    const response = axios.post(path, payload)
    return response
  },

  /**
   * Accept the Seller Outbound Return
   * @param {number} id - Seller Outbound Return ID
   * @param {object} payload - API payload
   */
  accept: async ({ id, values }) => {
    const path    = `${API_PATH}/${id}/accept`
    const payload = {
      seller_return: values
    }

    const response = axios.patch(path, payload)
    return response
  },

  /**
   * Ship the Seller Outbound Return with tracking no and proof files
   * @param {number} id - Seller Outbound Return ID
   * @param {object} payload - API payload
   */
  ship: async ({ id, values }) => {
    const path = `${API_PATH}/${id}/ship`
    const formData = buildFormDataPayload(values)

    const response = axios.patch(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    return response
  },

  /**
   * Mark the Seller Outbound Return as received by seller
   * @param {number} id - Seller Outbound Return ID
   */
  receive: async ({ id }) => {
    const path = `${API_PATH}/${id}/receive`

    const response = axios.patch(path)
    return response
  },

  /**
   * Edit Seller Outbound Return address / PC-owned fields
   * @param {number} id - Seller Outbound Return ID
   * @param {object} payload - API payload
   */
  update: async ({ id, values }) => {
    const path    = `${API_PATH}/${id}`
    const payload = {
      seller_return: values
    }

    const response = axios.patch(path, payload)
    return response
  },

  /**
   * Cancel Seller Outbound Return before it is shipped
   * @param {number} id - Seller Outbound Return ID
   */
  cancel: async ({ id }) => {
    const path = `${API_PATH}/${id}/cancel`

    const response = axios.patch(path)
    return response
  },

  /**
   * Cancel one Consignor Return item before shipment.
   * @param {number} id - Seller Outbound Return ID
   * @param {number} itemId - Seller Outbound Return Item ID
   */
  cancelItem: async ({ id, itemId }) => {
    const path = `${API_PATH}/${id}/items/${itemId}/cancel`

    const response = axios.patch(path)
    return response
  },

  /**
   * Download Consignor Return CSV export using the same list filters.
   * @param {object} tableState - ReactTable final state
   * @param {object} additionalFilters - Additional filters such as q/status/return_type
   */
  downloadCsv: async ({ tableState, additionalFilters = {} } = {}) => {
    const params = QueryParamsUtil.format(tableState, additionalFilters)
    const path   = `${API_PATH}/download_csv`

    const response = axios.get(path, { params, responseType: 'blob' })
    return response
  },

  /**
   * Download Consignor Return handover PDF.
   * @param {number} id - Seller Outbound Return ID
   */
  downloadHandoverPdf: async (id) => {
    const path = `${API_PATH}/${id}/download_handover_pdf`

    const response = axios.get(path, { responseType: 'blob' })
    return response
  }
}

export default SellerOutboundReturnsApiV2